import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo } from "react";
import { usePendingPaymentVerifications, usePendingTermVerifications } from "./verificationTaskApi";
import { usePendingModulePaymentVerifications } from "./modulePaymentVerificationApi";
import { scopePaymentVerificationsToCad } from "../accountUtils";
import VerificationTaskItem from "./VerificationTaskItem";
import { isRefundEntry } from "../../Admissions/AddAdmissions/applicationFlow/termPaymentUtils";
import { creditMoveTermNumbers } from "../../Admissions/AddAdmissions/applicationFlow/creditAdjustmentUtils";

const MODULE_DISPLAY_NAMES = {
  VISA: "Visa",
  INSURANCE: "Insurance",
  SKILL_ASSESSMENT: "Skill Assessment",
  RPL_CLIENT: "RPL",
};

const creditMoveLabel = (item) => {
  const { from, to } = creditMoveTermNumbers(item);
  return `Credit move: T${from ?? "?"} to T${to ?? "?"}`;
};

const formatEntryLabel = (payment, termLabel) => {
  const refund = isRefundEntry(payment?.entryType);
  const noun = refund ? "Refund" : "Payment";
  const term = termLabel ? ` — ${termLabel}` : "";
  return `${noun}${term}`;
};

const Section = ({ title, items, clientId, canAct, onActionComplete }) => {
  if (!items.length) return null;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold text-app-text-muted uppercase tracking-wide">
        {title} ({items.length})
      </p>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <VerificationTaskItem
            key={item.key}
            item={item}
            clientId={clientId}
            canAct={canAct}
            onActionComplete={onActionComplete}
          />
        ))}
      </div>
    </div>
  );
};

const AccountVerificationModal = ({
  open,
  onClose,
  clientId,
  admissionId,
  clientAccountDetailId = null,
  canAct = false,
  autoCloseWhenEmpty = false,
  onActionComplete,
  /** When both are set, scopes the "Module payment verifications" section to one module record (e.g. one Visa application) instead of every module payment for the client. */
  scopeModuleType = null,
  scopeModuleId = null,
}) => {
  // The three lists below are refetched by the item itself, which awaits their invalidation before
  // it calls back — so the callback is passed straight through rather than re-requesting them here.
  const payments = usePendingPaymentVerifications(clientId, open && !!clientId);
  const terms = usePendingTermVerifications(clientId, open && !!clientId);
  const modulePayments = usePendingModulePaymentVerifications(clientId, open && !!clientId);

  // Scope to ONE account entry by its CAD id when provided — precise even when a multi-entry
  // admission shares a studentDbId. Falls back to admissionId (studentDbId) for non-scoped usage.
  const scopeByCad = clientAccountDetailId != null;

  // Opened from one module record's "N pending" chip, the answer is that record's payments — the
  // client's admission verifications are a different question asked from a different place.
  const scopedToOneModule = scopeModuleType != null && scopeModuleId != null;

  const paymentItems = useMemo(() => {
    if (scopedToOneModule) return [];
    // Per-entry view: scope strictly by CAD id (shared helper, also used by the count badge).
    // Non-scoped view (no CAD id): fall back to the admission's studentDbId.
    const scoped = scopeByCad
      ? scopePaymentVerificationsToCad(payments.data, clientAccountDetailId)
      : (payments.data ?? []).filter(
          (p) => admissionId == null || String(p.admissionId) === String(admissionId)
        );
    return scoped.map((p) => ({
      key: `pay-${p.payment.id}`,
      kind: "payment",
      payment: p.payment,
      term: p.term,
      creditAdjustment: p.creditAdjustment ?? null,
      clientName: p.clientName,
      admissionId: p.admissionId,
      termIndex: p.termIndex,
      label: p.creditAdjustment
        ? creditMoveLabel(p)
        : formatEntryLabel(p.payment, p.term?.termLabel),
    }));
  }, [payments.data, admissionId, scopeByCad, clientAccountDetailId, scopedToOneModule]);

  const termItems = useMemo(
    () =>
      (scopedToOneModule ? [] : terms.data ?? [])
        .filter((v) => {
          // Pending whole-term verifications are often still staged (no CAD id yet), so match by CAD
          // id when present, else by the admission's studentDbId from the snapshot.
          const derivedAdmissionId = v.submittedAccountDetail?.admissionValues?.studentDbId;
          if (scopeByCad) {
            return (
              String(v.clientAccountDetailId) === String(clientAccountDetailId) ||
              (v.clientAccountDetailId == null &&
                admissionId != null &&
                String(derivedAdmissionId) === String(admissionId))
            );
          }
          if (admissionId == null) return true;
          return String(derivedAdmissionId) === String(admissionId);
        })
        .map((v) => {
          const derivedAdmissionId = v.submittedAccountDetail?.admissionValues?.studentDbId;
          const derivedTermIndex =
            v.submittedAccountDetail?.admissionDetails?.[0]?.terms != null
              ? Number(
                  String(v.submittedAccountDetail.admissionDetails[0].terms).replace(/^[Tt]/, "")
                ) - 1
              : null;
          return {
            key: `term-${v.id}`,
            kind: "term",
            admissionId: derivedAdmissionId,
            termIndex: derivedTermIndex,
            label: "Whole-term verification",
          };
        }),
    [terms.data, admissionId, scopeByCad, clientAccountDetailId, scopedToOneModule]
  );

  const modulePaymentItems = useMemo(() => {
    const scoped = scopedToOneModule
      ? (modulePayments.data ?? []).filter(
          (p) => p.moduleType === scopeModuleType && String(p.moduleId) === String(scopeModuleId)
        )
      : modulePayments.data ?? [];
    return scoped.map((p) => ({
      key: `mp-${p.id}`,
      kind: "modulePayment",
      payment: p,
      moduleType: p.moduleType,
      moduleId: p.moduleId,
      clientAccountDetailId: p.clientAccountDetailId,
      // moduleLabel is what the record calls itself — a custom entry's own name rather than the
      // word "OTHER", which tells a verifier nothing about what they are approving.
      label: `${isRefundEntry(p.entryType) ? "Refund" : "Payment"} — ${
        p.moduleLabel || MODULE_DISPLAY_NAMES[p.moduleType] || p.moduleType
      }`,
    }));
  }, [modulePayments.data, scopedToOneModule, scopeModuleType, scopeModuleId]);

  const total = paymentItems.length + termItems.length + modulePaymentItems.length;
  const isLoading = payments.isLoading || terms.isLoading || modulePayments.isLoading;

  useEffect(() => {
    if (!open || !autoCloseWhenEmpty || isLoading || total > 0) return;
    onClose?.();
  }, [open, autoCloseWhenEmpty, isLoading, total, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Pending Verifications
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: (t) => t.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <CircularProgress size={32} />
          </div>
        ) : total === 0 ? (
          <p className="text-sm text-app-text-muted py-6 text-center">
            No pending verifications for this account.
          </p>
        ) : (
          <div className="flex flex-col gap-5 py-1">
            <Section
              title="Whole-term verifications"
              items={termItems}
              clientId={clientId}
              canAct={canAct}
              onActionComplete={onActionComplete}
            />
            <Section
              title="Payment & refund verifications"
              items={paymentItems}
              clientId={clientId}
              canAct={canAct}
              onActionComplete={onActionComplete}
            />
            <Section
              title="Module payment verifications"
              items={modulePaymentItems}
              clientId={clientId}
              canAct={canAct}
              onActionComplete={onActionComplete}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountVerificationModal;
